import { useDialogPortal, useTransactionFees, useGasEstimation, useTransactionExecution } from "@/hooks";
import { cancelExitEventEmitter, CANCEL_EXIT_EVENTS } from "@/events";
import {
    prepareCancelUnwindingTransaction,
    estimateCancelUnwindingTransactionGas,
    gatewayCancelUnwindingTransaction,
} from "@/services";

interface CancelExitTransactionProps {
    newUnwindingEpochs: number;
    unwindingTimestampMs: number;
}

export function useCancelExitTransaction({ newUnwindingEpochs, unwindingTimestampMs }: CancelExitTransactionProps) {
    const { closeDialog } = useDialogPortal();

    const {
        gasEstimate,
        isEstimatingGas,
        error: gasError,
    } = useGasEstimation({
        estimateGasFn: (walletClient) =>
            estimateCancelUnwindingTransactionGas(newUnwindingEpochs, unwindingTimestampMs, walletClient),
        prepareTransactionFn: async (walletClient) => {
            return prepareCancelUnwindingTransaction(newUnwindingEpochs, unwindingTimestampMs, walletClient);
        },
        hasApproval: true,
    });

    const { gasPriceUSD } = useTransactionFees({
        estimatedGas: gasEstimate,
    });

    const {
        transactionHash,
        isExecuting,
        error: transactionError,
        executeTransaction: executeCancelExitTransaction,
    } = useTransactionExecution({
        executeTransactionFn: (walletClient) =>
            gatewayCancelUnwindingTransaction(newUnwindingEpochs, unwindingTimestampMs, walletClient),
        onSuccess: (hash) => {
            cancelExitEventEmitter.emit(CANCEL_EXIT_EVENTS.SUCCESS, {
                transactionHash: hash,
                newUnwindingEpochs,
            });
            closeDialog();
        },
        errorMessage: "Failed to cancel exit",
    });

    return {
        gasPriceUSD,
        transactionHash,
        executeCancelExitTransaction,
        isLoading: isExecuting,
        isEstimatingGas,
        error: gasError || transactionError,
        gasEstimate,
    };
}
